import * as React from 'react';
import moment from 'moment';
import { ConsentRequestHistoryEntry } from '../../models/consentRequestHistory';
import { FormAnswersSummary } from '../FormAnswersSummary/FormAnswersSummary';
import { answersToMap } from '../../helpers/form/formRuntime';

const sameActor = (left: any, right: any) => !!left && !!right
  && String(left.userId) === String(right.userId) && String(left.permissionId) === String(right.permissionId);

export function getConsentHistory(message: any): ConsentRequestHistoryEntry[] {
  if (message.responseHistory?.length) return message.responseHistory;
  const entries: ConsentRequestHistoryEntry[] = [];
  const latestAt = message.actionPerformedBySetAt || message.invitationStatusSetAt;
  const latestIsStaff = message.updatedByStaff && sameActor(message.actionPerformedBy, message.staffUpdatedBy);
  if (message.firstResponseAt && (!latestAt || moment(message.firstResponseAt).valueOf() !== moment(latestAt).valueOf())) {
    entries.push({at: message.firstResponseAt, action: 'LEGACY_RESPONSE', actor: message.firstResponseBy,
      actorData: message.firstResponseByData, actorRole: message.firstResponseBy ? sameActor(message.firstResponseBy, message.staffUpdatedBy) ? 'STAFF' : 'PARENT' : 'UNKNOWN'});
  }
  if (message.updatedByStaff && message.staffUpdatedAt && (!latestIsStaff || !latestAt || moment(message.staffUpdatedAt).valueOf() !== moment(latestAt).valueOf())) {
    entries.push({at: message.staffUpdatedAt, action: 'LEGACY_STAFF_UPDATE', actor: message.staffUpdatedBy,
      actorData: message.staffUpdatedByData, actorRole: 'STAFF'});
  }
  if (latestAt && message.actionPerformedBy) {
    entries.push({at: latestAt, action: 'RECORDED_RESPONSE', actorRole: latestIsStaff ? 'STAFF' : 'PARENT',
      actor: message.actionPerformedBy, actorData: message.actionPerformedByData,
      invitationStatus: message.invitationStatus, fields: message.fields, formDefinition: message.formDefinition,
      formAnswers: message.formAnswers, declineReason: message.declineReason});
  } else if (latestAt && message.invitationStatus === 'OUTDATED') {
    entries.push({at: latestAt, actorRole: 'SYSTEM', action: 'EXPIRED', invitationStatus: 'OUTDATED'});
  }
  return entries.sort((a, b) => moment(a.at).valueOf() - moment(b.at).valueOf());
}

function actionText(entry: ConsentRequestHistoryEntry): string {
  if (entry.action === 'ACCEPT') return 'Accepted consent';
  if (entry.action === 'REJECT') return 'Rejected consent';
  if (entry.action === 'EDIT') return 'Edited answers';
  if (entry.action === 'REOPEN') return 'Returned consent to pending';
  if (entry.action === 'EXPIRED') return 'Request expired';
  if (entry.action === 'LEGACY_STAFF_UPDATE') return 'Updated by staff';
  if (entry.action === 'LEGACY_RESPONSE') return 'First response recorded';
  return `Recorded response${entry.invitationStatus ? ` — ${entry.invitationStatus === 'NOT_READY' ? 'Pending' : entry.invitationStatus.toLowerCase()}` : ''}`;
}

function answerText(value: any): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(answerText).join(', ');
  if (typeof value === 'object') return value.name || JSON.stringify(value);
  return String(value);
}

export const ConsentRequestHistory: React.FC<{message: any}> = ({message}) => {
  const entries = getConsentHistory(message);
  return <ol className="list-unstyled mb-0">
    {message.createdAt && moment(message.createdAt).isValid() ? <li className="mb-3"><strong>Request sent</strong><br/><small className="text-muted">{moment(message.createdAt).format('D MMM YYYY, HH:mm')}</small></li> : null}
    {entries.map((entry, index) => <li className="mb-3" key={index}>
      <strong>{actionText(entry)}</strong><br/>
      <span>{entry.actorRole === 'STAFF' ? 'Staff' : entry.actorRole === 'PARENT' ? 'Parent' : entry.actorRole === 'SYSTEM' ? 'System' : 'Responder'}{entry.actorData?.fullName ? ` · ${entry.actorData.fullName}` : ''}</span>
      <br/><small className="text-muted">{moment(entry.at).format('D MMM YYYY, HH:mm:ss')}</small>
      {entry.declineReason ? <p className="mb-0">Reason: {entry.declineReason}</p> : null}
      {entry.fields !== undefined || entry.formDefinition ? <details className="mt-2"><summary>View recorded answers</summary>
        {entry.formDefinition ? <FormAnswersSummary definition={{...entry.formDefinition, logic: [], elements: entry.formDefinition.elements.filter(element => element.type !== 'PAGE_BREAK')}} answers={answersToMap(entry.formAnswers || [])} /> :
          entry.fields?.length ? <dl className="mb-0">{entry.fields.map((field, fieldIndex) => <div key={fieldIndex}><dt>{field.heading}</dt><dd>{answerText(field.value)}</dd></div>)}</dl> : <p>No additional questions.</p>}
      </details> : null}
      {['LEGACY_RESPONSE', 'LEGACY_STAFF_UPDATE'].includes(entry.action) ? <small className="d-block text-muted">Earlier answer details were not recorded.</small> : null}
    </li>)}
    {!entries.length && !message.createdAt ? <li className="text-muted">No changes recorded.</li> : null}
  </ol>;
};
